/**
 * Receipt field visibility — which optional blocks the cashier tape prints,
 * configured per branch in `branches.receipt_fields`.
 *
 * Per-branch rather than a tenant feature on purpose: the other two things that
 * shape the tape (`branches.receipt_header`, the company block, and
 * `branches.order_prefix`, the order-number series) are already per-branch, and
 * the cashier SPA receives the whole `Branch` row from `GET /branches` — so
 * these flags reach all three receipt call sites (checkout, release, reprint)
 * without a new endpoint. A stockist tape and a main-store tape can also
 * legitimately differ.
 *
 * Every flag defaults ON except `showRemarks`. These fields are being ADDED to
 * a tape that didn't have them, so an unconfigured branch (`{}`) must print
 * them — a default-off flag would ship the feature invisible. Remarks are the
 * exception: they're a free-text internal memo, not customer-facing.
 */

export type ReceiptFieldFlags = {
  /** Customer / member the sale was rung up for. */
  showMemberName: boolean;
  /** Staff account that processed the transaction. */
  showCashierName: boolean;
  /** Branch (or stockist) the sale belongs to. */
  showBranchName: boolean;
  /** Transaction date + time. */
  showDateTime: boolean;
  /** The POS "Remarks" note. Off by default — it's an internal memo. */
  showRemarks: boolean;
};

export const RECEIPT_FIELDS_DEFAULT: ReceiptFieldFlags = {
  showMemberName: true,
  showCashierName: true,
  showBranchName: true,
  showDateTime: true,
  showRemarks: false,
};

/**
 * Merge a stored blob forward onto the defaults. A branch saved before this
 * feature existed holds `{}`, and a branch saved by an older build is missing
 * whichever keys were added since — both must resolve to the default rather
 * than to `undefined` (which reads as false and would silently blank the tape).
 */
export function resolveReceiptFields(
  stored: Partial<ReceiptFieldFlags> | null | undefined,
): ReceiptFieldFlags {
  if (!stored || typeof stored !== 'object') return { ...RECEIPT_FIELDS_DEFAULT };
  const out = { ...RECEIPT_FIELDS_DEFAULT };
  for (const key of Object.keys(RECEIPT_FIELDS_DEFAULT) as Array<keyof ReceiptFieldFlags>) {
    if (typeof stored[key] === 'boolean') out[key] = stored[key];
  }
  return out;
}
